import { Input, InputProps } from "../../atoms/Input";
import { CheckInput, CheckInputProps } from "../../atoms/CheckInput";
import { Label } from "../../atoms/Label";
import { Typography } from "../../atoms/Typography";
import { Snackbar } from "../../atoms/Snackbar";
import styled from "styled-components";

import { PageBody } from "../PageBody";
import { ValidationErrors } from "../../../types";
import okIcon from "../../../assets/icons/ok.png";
import errorIcon from "../../../assets/icons/alert.png";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { Button } from "../../atoms/Button";
import { defaultTheme } from "../../themes";

/**
 * ウェルカムメール再送ページ
 */

export type ResendWelcomeMailTarget = {
  id: string;
  contractId: string;
  name: string;
  address: string;
  managerMailAddress: string;
  managerName: string;
  pdt: {
    id: string;
    name: string;
    iconPath: string;
  };
};

export type ValidatingForm = {
  otherMailAddress: string | undefined;
};

export type ResendWelcomeMailPageProps = {
  resendWelcomeMailTarget: ResendWelcomeMailTarget;
  resendWelcomeMailTargetItems?: (keyof ResendWelcomeMailTarget)[];
  onResendWelcomeMailButtonClick: (mailAddress?: string) => void;
  resendWelcomeMailProcessing: boolean;
  validationErrors: ValidationErrors<ValidatingForm>;
  resetPassword: boolean;
  setResetPassword: (reset: boolean) => void;
  otherMailAddress: string;
  setOtherMailAddress: (mail: string) => void;
  isFormValid: boolean;
  snackBar: {
    type: "ok" | "error";
    message: string;
    isOpen: boolean;
    close: () => void;
  } | null;
  onCancelButtonClick: () => void;
  isAdminVisible: boolean;
};

const ContentWrapper = styled.div`
  padding: 20px 40px;
  display: flex;
  flex-wrap: wrap;
`;

const ItemWrapper = styled.div<{
  width?: string;
  $marginBottom?: string;
  $caution?: boolean;
  $bold?: boolean;
}>`
  ${(props) => props.$bold && `font-weight: bold`};
  ${(props) => props.$caution && `color: ${props.theme.colors.caution}`};
  ${(props) => props.$marginBottom && `margin-bottom: ${props.$marginBottom}`};
  ${(props) => props.width && `width: ${props.width}`};
  padding: 0 30px 5px 0;
`;

const ButtonWrapper = styled.div<{ $marginTop?: string; width?: string }>`
  ${(props) => props.$marginTop && `margin-top: ${props.$marginTop}`};
  width: ${(props) => props.width || "214px"};
  height: 44px;
`;

const CautionText = styled.p<{ $marginBottom?: string }>`
  color: ${defaultTheme.colors.caution};
  ${(props) => props.$marginBottom && `margin-bottom: ${props.$marginBottom}`};
  line-height: 1.5;
`;

const PdtName = styled.p`
  align-items: center;
  display: flex;
  margin-bottom: 40px;
`;

const PdtIcon = styled.img`
  border-radius: 5px;
  border: 1px solid #d6dde8;
  margin-right: 10px;
`;

export function ResendWelcomeMailPage({
  resendWelcomeMailTarget,
  onResendWelcomeMailButtonClick,
  resendWelcomeMailProcessing,
  validationErrors,
  resetPassword,
  otherMailAddress,
  setResetPassword,
  setOtherMailAddress,
  isFormValid,
  snackBar = null,
  onCancelButtonClick,
  isAdminVisible,
}: ResendWelcomeMailPageProps) {
  const checkProps: CheckInputProps = {
    value: "true",
    onChange: () => setResetPassword(!resetPassword),
    label: "パスワードをリセットする",
    checked: resetPassword,
  };
  const inputProps: InputProps = {
    onChange: (e) => setOtherMailAddress(e.target.value),
    value: otherMailAddress,
    error: !validationErrors.otherMailAddress.isValid,
    errorMessages: validationErrors.otherMailAddress.message,
  };
  return (
    <>
      <PageBody
        title={"ウェルカムメール再送"}
        buttonTitle="中止"
        buttonType="danger"
        buttonOutline
        buttonIcon={
          <FontAwesomeIcon icon={faTimes} color={defaultTheme.colors.caution} />
        }
        onButtonClick={() => {
          onCancelButtonClick();
        }}
      >
        <>
          <ContentWrapper>
            <PdtName>
              <PdtIcon
                src={resendWelcomeMailTarget.pdt.iconPath}
                alt={resendWelcomeMailTarget.pdt.name}
                width="40"
                height="40"
              />{" "}
              {resendWelcomeMailTarget.pdt.name}
            </PdtName>
            <ItemWrapper width="100%" $marginBottom="10px">
              <Label text={"医療機関ID"} margin="0 0 7px 0" />
              <p>{resendWelcomeMailTarget.id}</p>
            </ItemWrapper>
            <ItemWrapper>
              <Label text={"医療機関名"} margin="0 0 7px 0" />
              <p>{resendWelcomeMailTarget.name}</p>
            </ItemWrapper>
            <ItemWrapper>
              <Label text={"住所"} margin="0 0 7px 0" />
              <p>{resendWelcomeMailTarget.address}</p>
            </ItemWrapper>
            <ItemWrapper>
              <Label text={"製品管理責任者"} margin="0 0 7px 0" />
              <p>{resendWelcomeMailTarget.managerName}</p>
            </ItemWrapper>
          </ContentWrapper>
          <ContentWrapper>
            <ItemWrapper width="100%" $marginBottom="12px">
              <Label text={"連絡先メールアドレス"} margin="0 0 7px 0" />
              <p>{resendWelcomeMailTarget.managerMailAddress}</p>
            </ItemWrapper>
            <ItemWrapper width="100%" $caution $bold>
              <CautionText $marginBottom="12px">
                ウェルカムメールの再送を行うとPdt契約時に登録されている製品管理責任者のパスワードがリセットされます。
                <br />
                製品管理責任者についてはウェルカムメールに記載のシークレットコードを使用して再度パスワードの設定を行う必要があるため、
                <br />
                すでに利用されているPdtで再送を行う場合はご注意ください。
              </CautionText>
              <CheckInput {...checkProps} />
            </ItemWrapper>
            <ButtonWrapper $marginTop="12px">
              <Button
                onClick={() => onResendWelcomeMailButtonClick()}
                disabled={resendWelcomeMailProcessing}
                buttonType={"primary"}
              >
                <Typography size={1}>ウェルカムメールを再送する</Typography>
              </Button>
            </ButtonWrapper>
          </ContentWrapper>
          {isAdminVisible && (
            <ContentWrapper>
              <Label
                text={
                  "登録メールアドレス以外にウェルカムメールを送る場合、以下にメールアドレスを入力してください。"
                }
                margin="0 0 7px 0"
              />
              <ItemWrapper width="100%">
                <Input {...inputProps} />
              </ItemWrapper>
              <ButtonWrapper width="334px">
                <Button
                  onClick={() =>
                    onResendWelcomeMailButtonClick(otherMailAddress)
                  }
                  disabled={resendWelcomeMailProcessing || !isFormValid}
                  buttonType={"primary"}
                >
                  <Typography size={1}>
                    登録メールアドレス以外にウェルカムメールを送る
                  </Typography>
                </Button>
              </ButtonWrapper>
            </ContentWrapper>
          )}
        </>
      </PageBody>
      {!!snackBar && (
        <Snackbar
          isOpen={snackBar.isOpen}
          icon={
            <img
              src={snackBar.type === "ok" ? okIcon : errorIcon}
              width="18"
              height="18"
              alt={snackBar.type}
            />
          }
          text={snackBar.message}
          close={snackBar.close}
        />
      )}
    </>
  );
}
